import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { VehicleTracking, GPSDevice } from '@/api/gpsApi';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in React Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface VehicleMapModalProps {
  isOpen: boolean;
  onClose: () => void;
  device: GPSDevice;
  trackingData: VehicleTracking | null;
}

const VehicleMapModal: React.FC<VehicleMapModalProps> = ({
  isOpen,
  onClose,
  device,
  trackingData
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const hasLocation = trackingData?.location?.latitude && trackingData?.location?.longitude;
  const center: [number, number] = hasLocation 
    ? [trackingData.location.latitude, trackingData.location.longitude]
    : [-1.2921, 36.8219]; // Default to Nairobi

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Vehicle Location: {device.deviceName}
            </h2>
            <p className="text-sm text-gray-600">IMEI: {device.imei}</p>
          </div>
          <Button onClick={onClose} variant="ghost" size="sm">
            <XMarkIcon className="w-5 h-5" />
          </Button>
        </div>

        {/* Map Content */}
        <div className="p-6">
          {hasLocation ? (
            <div className="h-96 rounded-lg overflow-hidden border border-gray-200">
              <MapContainer
                center={center}
                zoom={15}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker position={center}>
                  <Popup>
                    <div className="p-2">
                      <h3 className="font-semibold">{device.deviceName}</h3>
                      <p className="text-sm text-gray-600">IMEI: {device.imei}</p>
                      <div className="mt-2 text-sm">
                        <p><strong>Status:</strong> {trackingData.status.dataStatus}</p>
                        <p><strong>Speed:</strong> {trackingData.location.speed} km/h</p>
                        <p><strong>Course:</strong> {trackingData.location.course}°</p>
                        <p><strong>ACC:</strong> {trackingData.status.acc}</p>
                        {trackingData.status.battery && (
                          <p><strong>Battery:</strong> {trackingData.status.battery}%</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          Last Update: {new Date(trackingData.timing.gpsTime || '').toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              </MapContainer>
            </div>
          ) : (
            <div className="h-96 flex items-center justify-center bg-gray-100 rounded-lg">
              <div className="text-center">
                <div className="text-gray-400 mb-2">📍</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Location Data</h3>
                <p className="text-gray-600">
                  GPS location is not available for this vehicle.
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Device may be offline or GPS signal may be weak.
                </p>
              </div>
            </div>
          )}

          {/* Vehicle Info */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Device Info</h4>
              <div className="text-sm space-y-1">
                <p><strong>Type:</strong> {device.deviceType}</p>
                <p><strong>Plate:</strong> {device.plateNumber || 'N/A'}</p>
                <p><strong>SIM:</strong> {device.simCard || 'N/A'}</p>
              </div>
            </div>
            
            {trackingData && (
              <>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Current Status</h4>
                  <div className="text-sm space-y-1">
                    <p><strong>Status:</strong> 
                      <span className={`ml-1 ${trackingData.status.dataStatus === 'OK' ? 'text-green-600' : 'text-red-600'}`}>
                        {trackingData.status.dataStatus}
                      </span>
                    </p>
                    <p><strong>Speed:</strong> {trackingData.location.speed} km/h</p>
                    <p><strong>ACC:</strong> {trackingData.status.acc}</p>
                    <p><strong>Charging:</strong> {trackingData.status.charging}</p>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Coordinates</h4>
                  <div className="text-sm space-y-1">
                    <p><strong>Latitude:</strong> {trackingData.location.latitude.toFixed(6)}</p>
                    <p><strong>Longitude:</strong> {trackingData.location.longitude.toFixed(6)}</p>
                    <p><strong>Course:</strong> {trackingData.location.course}°</p>
                    <p className="text-xs text-gray-500 mt-2">
                      GPS Time: {new Date(trackingData.timing.gpsTime || '').toLocaleString()}
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <Button onClick={onClose} variant="secondary">
            Close
          </Button>
          {hasLocation && (
            <Button
              onClick={() => {
                const url = `https://www.google.com/maps?q=${trackingData!.location.latitude},${trackingData!.location.longitude}`;
                window.open(url, '_blank');
              }}
              variant="primary"
            >
              Open in Google Maps
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VehicleMapModal;